import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextLayoutEventData } from 'react-native';
import { CHECKBOX_ON_FILL, ON_ACCENT_TEXT } from '../../styles/accentText';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useI18n } from '../../i18n';
import { legalConsentKeys, legalPhraseSegments, splitMarketingConsent } from '../../services/consentLogic';
import { isForeignContour } from '../../legal/accepted';

/**
 * Принятие пользовательского соглашения на экранах входа и регистрации
 * (CORE-840).
 *
 * App Store Guideline 1.2 требует, чтобы условия использования предъявлялись
 * ДО регистрации или входа, а сами условия прямо декларировали нулевую
 * терпимость к оскорбительному контенту и абьюзивным пользователям. Поэтому:
 *  - на регистрации — обязательный чекбокс (`LegalConsentCheckbox`), без него
 *    кнопка «Зарегистрироваться» неактивна;
 *  - на входе — строка-уведомление (`LegalConsentNotice`).
 * И там, и там текст соглашения открывается по нажатию, без ухода с экрана.
 *
 * CORE-1303 — абзац про нулевую терпимость с самой формы регистрации убран
 * (решение владельца): на первом экране он читался как предупреждение тому,
 * кто ещё ничего не сделал. Требование Guideline 1.2 закрывает сам документ —
 * раздел 5 «Условий использования», который открывается отсюда по ссылке.
 * Ключ `legal.zeroTolerance` намеренно оставлен во всех трёх локалях: если
 * App Review потребует строку обратно, вернуть её — это один <Text> здесь.
 *
 * Компоненты не знают, КАК открыть документ: нативные экраны показывают его
 * в модале (LegalDocsModal), веб уводит на маршрут /legal/:docId.
 */

/**
 * CORE-1929 — `marketing-consent`: полный текст согласия на рекламу, ссылка у рекламной галочки.
 * CORE-1932 — `personal-data-consent`: согласие на обработку ПДн, ссылка у первой галочки.
 */
export type LegalDocKind = 'terms-of-use' | 'privacy-policy' | 'marketing-consent' | 'personal-data-consent';

/**
 * CORE-1733 (возврат QA 23.10, замечание ревью 58868) — отмеченный чекбокс на
 * вебе один для обоих согласий и обеих тем: фирменная заливка с тёмной галочкой
 * (6.3:1). Раньше заливка шла из цвета ссылки или акцента экрана — коричневый
 * #9A421F в светлой, #F0A06D с белой галочкой 2.11:1 в тёмной, а рекламный
 * чекбокс на регистрации был #F25C05. Натив — прежние цвета и белая галочка.
 */
const WEB = Platform.OS === 'web';
const checkedFill = (nativeColor: string): string => (WEB ? CHECKBOX_ON_FILL : nativeColor);
const CHECK_MARK = WEB ? ON_ACCENT_TEXT : '#FFFFFF';

/**
 * iOS кладёт подчёркивание вплотную к буквам и берёт толщину из шрифта (у
 * жирной ссылки — толстая), а ни отступа, ни толщины RN не даёт. Поэтому на
 * iOS ссылки во фразе без подчёркивания, а под каждой строкой — срез высотой
 * UNDERLINE_WIDTH на UNDERLINE_GAP ниже базовой линии (её отдаёт onTextLayout)
 * из прозрачной копии фразы, где у ссылок залит фон: в срезе видны только
 * линии под ссылками. Текст и ширина те же — переносы совпадают. Срез обрезан
 * по самому тексту строки (x/width из onTextLayout): у перенесённой ссылки iOS
 * тянет фон до края строки.
 */
const IOS = Platform.OS === 'ios';
const UNDERLINE_GAP = 3;
const UNDERLINE_WIDTH = StyleSheet.hairlineWidth;

interface LinksProps {
  /** Вводная часть фразы: «Я принимаю …» либо «Входя в аккаунт, вы принимаете …». */
  prefix: string;
  /** CORE-1932 — третья ссылка, на согласие на обработку ПДн (первая галочка, российский контур). */
  withPersonalDataConsent?: boolean;
  onOpenDoc: (doc: LegalDocKind) => void;
  textColor: string;
  linkColor: string;
  /** Ссылки крупнее 13 px на вебе — там строка живёт в более просторной вёрстке. */
  fontSize?: number;
  testIDPrefix?: string;
  align?: 'center' | 'left';
}

/**
 * «… Условия использования и Политику конфиденциальности» — две нажимаемые ссылки;
 * с `withPersonalDataConsent` — три (CORE-1932).
 */
const LegalLinks: React.FC<LinksProps> = ({
  prefix,
  withPersonalDataConsent = false,
  onOpenDoc,
  textColor,
  linkColor,
  fontSize = 13,
  testIDPrefix = 'legal',
  align = 'left',
}) => {
  const { t } = useI18n('auth');
  const [lines, setLines] = useState<TextLayoutEventData['lines']>([]);
  const [boxWidth, setBoxWidth] = useState(0);
  // `copy` — прозрачная копия для линий на iOS: фон у ссылок, без нажатий и testID.
  const phrase = (copy: boolean) => {
    const link = copy
      ? { color: 'transparent', fontWeight: '700' as const, backgroundColor: linkColor }
      : { color: linkColor, fontWeight: '700' as const, textDecorationLine: IOS ? 'none' as const : 'underline' as const };
    const press = (doc: LegalDocKind, id: string) =>
      copy ? {} : { testID: `${testIDPrefix}${id}`, accessibilityRole: 'link' as const, onPress: () => onOpenDoc(doc) };
    return (
      <Text
        style={{ color: copy ? 'transparent' : textColor, fontSize, lineHeight: fontSize + 7, textAlign: align }}
        onTextLayout={IOS && !copy ? (e) => {
          const next = e.nativeEvent.lines;
          setLines((prev) => (
            prev.length === next.length && prev.every((l, i) => (
              l.x === next[i].x && l.y === next[i].y && l.width === next[i].width && l.ascender === next[i].ascender
            ))
              ? prev
              : next
          ));
        } : undefined}
      >
        {legalPhraseSegments(t, prefix, withPersonalDataConsent).map((seg, i) => (
          seg.doc && seg.linkId ? (
            <Text key={i} style={link} {...press(seg.doc, seg.linkId)}>
              {seg.text}
            </Text>
          ) : (
            <React.Fragment key={i}>{seg.text}</React.Fragment>
          )
        ))}
      </Text>
    );
  };
  if (!IOS) return phrase(false);
  return (
    <View onLayout={(e) => setBoxWidth(e.nativeEvent.layout.width)}>
      {phrase(false)}
      {boxWidth > 0 && lines.map((line, i) => {
        const top = line.y + line.ascender + UNDERLINE_GAP;
        return (
          <View
            key={i}
            pointerEvents="none"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={[styles.underlineSlice, { top, left: line.x, width: line.width }]}
          >
            <View style={[styles.underlineCopy, { top: -top, left: -line.x, width: boxWidth }]}>
              {phrase(true)}
            </View>
          </View>
        );
      })}
    </View>
  );
};

interface CheckboxProps {
  checked: boolean;
  onToggle: () => void;
  onOpenDoc: (doc: LegalDocKind) => void;
  textColor: string;
  linkColor: string;
  /** Цвет рамки невыбранного чекбокса. */
  borderColor: string;
  fontSize?: number;
}

/** Обязательное согласие на регистрации: чекбокс и фраза со ссылками. */
export const LegalConsentCheckbox: React.FC<CheckboxProps> = ({
  checked,
  onToggle,
  onOpenDoc,
  textColor,
  linkColor,
  borderColor,
  fontSize = 13,
}) => {
  const { t } = useI18n('auth');
  // CORE-1932 — в российском контуре галочка называет три документа. CORE-1560 —
  // текст юриста с заменённым началом: «Отмечая это поле, я принимаю Условия
  // использования, … и даю согласие на обработку персональных данных. Подробнее об
  // обработке данных читайте в Политике» (`legalPhraseSegments`). Вне российского контура (английские
  // Условия AGAPE.IO Limited) согласия на ПДн нет — оно есть только у АО и только
  // по-русски (CORE-1559): там галочка прежняя. Правило то же, что выбирает
  // редакции для записи (`acceptedRegistrationDocuments`); язык в нём есть, и
  // useI18n перерисует галочку при его смене.
  const withPd = !isForeignContour();
  return (
    <View style={styles.consentRow}>
      <TouchableOpacity
        testID="legalConsentCheckbox"
        onPress={onToggle}
        hitSlop={10}
        accessibilityRole="checkbox"
        accessibilityState={{ checked }}
        accessibilityLabel={t(legalConsentKeys(withPd).a11y)}
        style={[
          styles.box,
          { borderColor: checked ? checkedFill(linkColor) : borderColor, backgroundColor: checked ? checkedFill(linkColor) : 'transparent' },
        ]}
      >
        {checked && <Ionicons name="checkmark" size={14} color={CHECK_MARK} />}
      </TouchableOpacity>
      <View style={styles.consentTextWrap}>
        <LegalLinks
          prefix={t(legalConsentKeys(withPd).prefix)}
          withPersonalDataConsent={withPd}
          onOpenDoc={onOpenDoc}
          textColor={textColor}
          linkColor={linkColor}
          fontSize={fontSize}
        />
      </View>
    </View>
  );
};

interface MarketingProps {
  checked: boolean;
  onToggle: () => void;
  /** CORE-1929 — открыть полный текст согласия (`marketing-consent`). */
  onOpenDoc: (doc: LegalDocKind) => void;
  textColor: string;
  accentColor: string;
  /** Цвет ссылки на полный текст согласия. */
  linkColor: string;
  borderColor: string;
  fontSize?: number;
}

// На iOS подчёркивание RN ложится вплотную к буквам (см. LegalLinks) — там ссылку
// отличают цвет и жирность.
const MARKETING_LINK_DECORATION = IOS ? 'none' as const : 'underline' as const;

/**
 * CORE-1657 — необязательное согласие на рекламные сообщения, вторая галочка
 * под обязательной. Текст — от юриста, дословно; по умолчанию не отмечена, и
 * регистрация проходит без неё.
 *
 * CORE-1929 — слова внутри строки (`legal.marketingConsentLink`) ведут на
 * полный текст согласия (/legal/marketing-consent; в приложении — модал).
 *
 * CORE-1560 — с 30.10 текст «Я даю согласие на получение рекламы от АО «Цифровая
 * ИИ Трансформация»» (ответ юриста на вопрос 31), ссылка — «согласие на
 * получение рекламы». Одинаково на регистрации и после соцвхода.
 */
export const MarketingConsentCheckbox: React.FC<MarketingProps> = ({
  checked,
  onToggle,
  onOpenDoc,
  textColor,
  accentColor,
  linkColor,
  borderColor,
  fontSize = 13,
}) => {
  const { t } = useI18n('auth');
  const [before, link, after] = splitMarketingConsent(t('legal.marketingConsent'), t('legal.marketingConsentLink'));
  return (
    <View style={styles.consentRow}>
      <TouchableOpacity
        testID="marketingConsentCheckbox"
        onPress={onToggle}
        hitSlop={10}
        accessibilityRole="checkbox"
        accessibilityState={{ checked }}
        accessibilityLabel={t('legal.marketingConsent')}
        style={[
          styles.box,
          { borderColor: checked ? checkedFill(accentColor) : borderColor, backgroundColor: checked ? checkedFill(accentColor) : 'transparent' },
        ]}
      >
        {checked && <Ionicons name="checkmark" size={14} color={CHECK_MARK} />}
      </TouchableOpacity>
      <View style={styles.consentTextWrap}>
        <Text style={{ color: textColor, fontSize, lineHeight: fontSize + 7 }}>
          {before}
          <Text
            testID="marketingConsentLink"
            accessibilityRole="link"
            onPress={() => onOpenDoc('marketing-consent')}
            style={{ color: linkColor, fontWeight: '700', textDecorationLine: MARKETING_LINK_DECORATION }}
          >
            {link}
          </Text>
          {after}
        </Text>
      </View>
    </View>
  );
};

interface NoticeProps {
  onOpenDoc: (doc: LegalDocKind) => void;
  textColor: string;
  linkColor: string;
  fontSize?: number;
  align?: 'center' | 'left';
}

/** Уведомление на экране входа: «Входя в аккаунт, вы принимаете …». */
export const LegalConsentNotice: React.FC<NoticeProps> = ({
  onOpenDoc,
  textColor,
  linkColor,
  fontSize = 12,
  align = 'center',
}) => {
  const { t } = useI18n('auth');
  return (
    <View style={styles.notice}>
      <LegalLinks
        prefix={t('legal.loginPrefix')}
        onOpenDoc={onOpenDoc}
        textColor={textColor}
        linkColor={linkColor}
        fontSize={fontSize}
        testIDPrefix="loginLegal"
        align={align}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  consentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    // Оптически выравнивает квадрат с первой строкой текста.
    marginTop: 1,
  },
  consentTextWrap: {
    flex: 1,
  },
  notice: {
    marginTop: 16,
    paddingHorizontal: 8,
  },
  underlineSlice: {
    position: 'absolute',
    height: UNDERLINE_WIDTH,
    overflow: 'hidden',
  },
  underlineCopy: {
    position: 'absolute',
  },
});
